import { Tabs, TabsProps } from 'antd';
import { useTranslation } from 'react-i18next';
import {
  DettaglioProjectDto,
  DettagliProgettoDtoStatoEnum,
  ExternalCostDto,
  FilterDtoTipologiaEnum,
} from '@api/client';
import { getCompetenzeComplessive, getCompetenzePerAnno } from '../../_redux/actions';
import { TableProjectKeys } from 'enums/project.enum';
import { useAppDispatch, useAppSelector } from 'core/store/hooks';
import { currentProjectTypeSelector } from 'pages/private/project-list/_redux/selectors';
import LinksButton from 'shared/design-system/components/links-button';
import YearSelect from 'shared/_components/year-select';
import useSaveWbs from '../../_hooks/wbs/useSaveWbs';

interface Props {
  projectDetails: DettaglioProjectDto | null;
  readOnly: boolean | undefined;
  idProject: number | undefined;
  costsData: ExternalCostDto[] | null | undefined;
  isWorkInProgress: boolean;
  isBillingDirty: boolean;
  table: TableProjectKeys;
  years: (number | undefined)[];
  economicDataYear: number | 'all';
  setEconomicDataYear: React.Dispatch<React.SetStateAction<number | 'all'>>;
  saveBozza: (idProject: number | undefined, costsData: ExternalCostDto[] | null | undefined) => void;
  setTable: React.Dispatch<React.SetStateAction<TableProjectKeys>>;
  setShowModalBilling: React.Dispatch<React.SetStateAction<boolean>>;
  setNewTabValue: React.Dispatch<React.SetStateAction<string | undefined>>;
}

const RevenuePlanCardHeader: React.FC<Props> = ({
  projectDetails,
  readOnly,
  idProject,
  costsData,
  isWorkInProgress,
  isBillingDirty,
  table,
  years,
  economicDataYear,
  setEconomicDataYear,
  saveBozza,
  setTable,
  setShowModalBilling,
  setNewTabValue,
}) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const currentProjectType = useAppSelector(currentProjectTypeSelector);
  const { saveWbs } = useSaveWbs();

  const projectDetailTabs: TabsProps['items'] = [
    {
      key: TableProjectKeys.OVERALL_DATA,
      label: `${t('project-detail.complessive-data')}`,
      icon: <i className="fa-solid fa-chart-column" />,
    },
    {
      key: TableProjectKeys.TEAM,
      label: `${t('project-detail.team')}`,
      icon: <i className="fa-solid fa-people-group" />,
    },
    {
      key: TableProjectKeys.EXTERNAL_COSTS,
      label: `${t('project-detail.external-costs')}`,
      icon: <i className="fa-solid fa-euro-sign" />,
    },
    ...(currentProjectType !== FilterDtoTipologiaEnum.Costo
      ? [
          {
            key: TableProjectKeys.REVENUE_PROGRESS,
            label: `${t('project-detail.revenue-progress')}`,
            icon: <i className="fa-solid fa-percent" />,
          },
        ]
      : []),
    {
      key: TableProjectKeys.WBS,
      label: `${t('project-detail.wbs')}`,
      icon: <i className="fa-solid fa-list-check" />,
    },
    ...(currentProjectType !== FilterDtoTipologiaEnum.Costo
      ? [
          {
            key: TableProjectKeys.BILLING,
            label: `${t('project-detail.billing')}`,
            icon: <i className="fa-regular fa-money-bill-1" />,
          },
        ]
      : []),
    {
      key: TableProjectKeys.EMITTED_BILLS,
      label: `${t('project-detail.emitted-bills')}`,
      icon: <i className="fa-solid fa-dollar-sign" />,
    },
    // Tab Assets nascosto
    // {
    //   key: TableProjectKeys.PRODUCTS_SUBSCRIPTIONS,
    //   label: `${t('project-detail.products-subscriptions')}`,
    //   icon: <i className="fa-solid fa-box-archive" />,
    // },
    {
      key: TableProjectKeys.NOTES,
      label: `${t('project-detail.notes')}`,
      icon: <i className="fa-regular fa-clipboard" />,
    },
  ];

  const canShowSaveDraft = () => {
    return (
      [TableProjectKeys.TEAM, TableProjectKeys.EXTERNAL_COSTS, TableProjectKeys.REVENUE_PROGRESS].includes(table) &&
      !isWorkInProgress &&
      projectDetails?.dettaglioOfferta?.stato !== DettagliProgettoDtoStatoEnum.IntranetPending
    );
  };

  const onChangeTab = (key: TableProjectKeys) => {
    if (isBillingDirty) {
      setShowModalBilling(true);
      setNewTabValue(key);
      return;
    }
    setTable(key);
  };

  return (
    <div className="d-flex align-items-center justify-content-between">
      <div className="col-10">
        <Tabs
          defaultActiveKey={TableProjectKeys.TEAM}
          items={projectDetailTabs}
          className="text-blue-links"
          onChange={(key: string) => onChangeTab(key as TableProjectKeys)}
        />
      </div>

      {canShowSaveDraft() && (
        <LinksButton
          className="btn-sm btn-primary btn-links px-2 ms-2"
          isDisabled={readOnly}
          onClick={() => saveBozza(idProject, costsData)}
        >
          <div className="d-flex align-items-center">
            <i className="fas fa-rotate-right pe-2" />
            {t('project-detail.save-draft')}
          </div>
        </LinksButton>
      )}
      {table === TableProjectKeys.WBS && (
        <LinksButton className="btn-sm btn-primary btn-links px-2 ms-2" isDisabled={readOnly} onClick={() => saveWbs()}>
          <div className="d-flex align-items-center">
            <i className="icon-save pe-2" />
            {t('Save WBS')}
          </div>
        </LinksButton>
      )}
      {[TableProjectKeys.OVERALL_DATA].includes(table) && (
        <div>
          <LinksButton
            className="btn-sm btn-icon text-blue-links"
            isDisabled={readOnly}
            onClick={() => {
              if (economicDataYear === 'all') {
                dispatch(getCompetenzeComplessive(idProject!));
              } else {
                dispatch(getCompetenzePerAnno({ projectId: idProject!, anno: economicDataYear }));
              }
            }}
          >
            <div className="d-flex align-items-center">
              <i className="fas fa-rotate-right" />
            </div>
          </LinksButton>

          <YearSelect years={years} defaultValue={'all'} onChange={(e) => setEconomicDataYear(e)} />
        </div>
      )}
    </div>
  );
};

export default RevenuePlanCardHeader;
